"use client";

import { CopyButton } from "@/components/tool-kit/primitives";
import {
  HINT,
  TABLE,
  TD,
  TEXTAREA,
  TH,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type {
  RenumberRow,
  RenumberStatus,
} from "@/lib/tool-kit/reference-checkers/citation-order";

/*
 * The output half of the Numbered Citation Order Checker: the old → new map and the
 * read-only boxes holding the renumbered text and list. Every word arrives in props
 * from the widget's strings.
 */

export interface RenumberMapLabels {
  heading: string;
  hint: string;
  old: string;
  new: string;
  status: string;
  statuses: Readonly<Record<RenumberStatus, string>>;
}

export function RenumberMap({
  rows,
  labels,
}: {
  rows: readonly RenumberRow[];
  labels: RenumberMapLabels;
}) {
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{labels.heading}</h3>
      <p className={HINT}>{labels.hint}</p>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {labels.old}
              </th>
              <th className={TH} scope="col">
                {labels.new}
              </th>
              <th className={TH} scope="col">
                {labels.status}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.old}>
                <td className={`${TD} font-mono`}>{row.old}</td>
                <td className={`${TD} font-mono`}>
                  {row.old === row.new ? row.new : `→ ${row.new}`}
                </td>
                <td className={TD}>{labels.statuses[row.status]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Renumbered({
  id,
  heading,
  label,
  value,
  copyLabel,
  copiedLabel,
}: {
  id: string;
  heading: string;
  label: string;
  value: string;
  copyLabel: string;
  copiedLabel: string;
}) {
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3} id={`${id}-heading`}>
        {heading}
      </h3>
      <textarea
        aria-label={label}
        className={TEXTAREA}
        id={id}
        readOnly
        rows={6}
        value={value}
      />
      <CopyButton copiedLabel={copiedLabel} label={copyLabel} text={value} />
    </div>
  );
}
